import { useMemo, useRef, useState, useEffect } from 'react';
import { Link } from '@tanstack/react-router';
import { Stethoscope, AlertTriangle, ChevronRight, Wifi, WifiOff, Radio, VolumeX } from 'lucide-react';
import { useAlertStore, getAlertKey } from '@/client/store/alertStore';
import { useEncoderWSSStore } from '@/client/store/encoderWSSStore';
import { useEncodersStore } from '@/client/store/encodersStore';
import { useShallow } from 'zustand/react/shallow';
import type { EncoderAlertPayload } from '@shared/types';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/client/components/ui/popover';
import { Badge } from '@/client/components/ui/badge';
import { Button } from '@/client/components/ui/button';
import { Separator } from '@/client/components/ui/separator';
import { cn } from '@/client/lib/utils';

const ALERT_TYPE_LABELS: Record<string, string> = {
  offline: 'Offline',
  zero_bitrate: 'Zero Bitrate',
  bitrate_too_high: 'Bitrate Too High',
  bitrate_drop: 'Bitrate Drop',
  bitrate_unstable: 'Unstable Bitrate',
  frame_rate_drop: 'Frame Rate Drop',
  resolution_change: 'Resolution Changed',
};

function formatTriageDuration(startedAt: number): string {
  const diff = Date.now() - startedAt;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  return `${minutes}m`;
}

export function TriageIndicator() {
  // Select raw state (not computed getters) to avoid infinite re-render
  const alertsMap = useAlertStore((s) => s.alerts);
  const triageSessions = useAlertStore((s) => s.triageSessions);
  const sseStatus = useAlertStore((s) => s.sseStatus);
  const mutedAlerts = useAlertStore((s) => s.mutedAlerts);

  // Get encoder names for display
  const getEncoder = useEncodersStore((s) => s.getEncoder);

  // Get WSS encoder monitoring data (flapping/connection issues)
  const encodersWithIssues = useEncoderWSSStore(
    useShallow((s) => {
      return Array.from(s.encoders.values())
        .filter((e) => e.state !== 'normal')
        .sort((a, b) => {
          // Alert first, then warning
          if (a.state === 'alert' && b.state !== 'alert') return -1;
          if (b.state === 'alert' && a.state !== 'alert') return 1;
          // Then by encoderID for stable ordering (prevents jumping on updates)
          return a.encoderID.localeCompare(b.encoderID);
        });
    })
  );

  // Compute derived values with useMemo (excluding muted alerts)
  const { alertCount, mutedCount, criticalCount, alerts } = useMemo(() => {
    let count = 0;
    let muted = 0;
    let critical = 0;
    const all: EncoderAlertPayload[] = [];

    for (const encoderAlerts of alertsMap.values()) {
      for (const alert of encoderAlerts.values()) {
        const key = getAlertKey(alert.encoderId, alert.alertType);
        if (mutedAlerts.has(key)) {
          muted++;
          continue; // Skip muted alerts from main list
        }

        count++;
        all.push(alert);
        if (alert.severity === 'critical') critical++;
      }
    }

    // Sort by severity (critical first) then by encoderId
    all.sort((a, b) => {
      const severityOrder: Record<string, number> = { critical: 0, warning: 1, info: 2 };
      const aSev = severityOrder[a.severity] ?? 3;
      const bSev = severityOrder[b.severity] ?? 3;
      if (aSev !== bSev) return aSev - bSev;
      return a.encoderId.localeCompare(b.encoderId);
    });

    return { alertCount: count, mutedCount: muted, criticalCount: critical, alerts: all };
  }, [alertsMap, mutedAlerts]);

  const triageCount = triageSessions.length;
  const hasAlerts = alertCount > 0;
  const hasTriage = triageCount > 0;
  // const hasAny = hasAlerts || hasTriage;

  // Track previous alert count to detect new alerts
  const prevAlertCountRef = useRef(alertCount);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Only animate if count increased (not on initial load when ref is 0)
    if (alertCount > prevAlertCountRef.current && prevAlertCountRef.current > 0) {
      setIsAnimating(true);
      // Remove animation class after it completes
      const timer = setTimeout(() => setIsAnimating(false), 600);
      prevAlertCountRef.current = alertCount;
      return () => clearTimeout(timer);
    }
    prevAlertCountRef.current = alertCount;
  }, [alertCount]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'gap-0 px-2 h-8',
            // hasAny && 'border-amber-300'
          )}
        >
          <Stethoscope className={cn(
            'h-4 w-4',
            // criticalCount > 0 ? 'text-red-600' : hasAlerts ? 'text-amber-600' :
            'text-muted-foreground',
            isAnimating && 'animate-alert-shake'
          )} />

          {/* Flapping/Monitoring count (blue) - only show if > 0 */}
          {encodersWithIssues.length > 0 && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <span className="min-w-[1.25rem] text-center text-xs font-medium rounded px-1 bg-blue-500 text-white">
                {encodersWithIssues.length}
              </span>
            </>
          )}

          {/* Anomalies count (amber) - only show if > 0 */}
          {alertCount > 0 && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <span
                className={cn(
                  'min-w-[1.25rem] text-center text-xs font-medium rounded px-1 bg-amber-500 text-white',
                  isAnimating && 'animate-alert-pop'
                )}
              >
                {alertCount}
              </span>
            </>
          )}

          {/* Triage count (red) - only show if > 0 */}
          {triageCount > 0 && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <span className="min-w-[1.25rem] text-center text-xs font-medium rounded px-1 bg-red-500 text-white">
                {triageCount}
              </span>
            </>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-80 p-0">
        {/* Header with SSE Status and View All */}
        <div className="px-3 py-2 border-b bg-muted/50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {sseStatus === 'connected' ? (
              <Badge variant="outline" className="bg-green-50 text-green-700 gap-1 text-xs h-5">
                <Wifi className="h-3 w-3" />
                Polling
              </Badge>
            ) : sseStatus === 'connecting' ? (
              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 gap-1 text-xs h-5">
                <Wifi className="h-3 w-3 animate-pulse" />
                Connecting...
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-gray-50 text-gray-500 gap-1 text-xs h-5">
                <WifiOff className="h-3 w-3" />
                Disconnected
              </Badge>
            )}
          </div>
          <Link
            to="/encoders/triage"
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            View All
            <ChevronRight className="h-3 w-3" />
          </Link>
        </div>

        {/* Connection Monitoring Section (WSS flapping) */}
        {encodersWithIssues.length > 0 && (
          <>
            <div className="p-3">
              <div className="flex items-center gap-2 mb-2">
                <Radio className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium">Connection Monitoring</span>
                <span className="ml-auto text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                  {encodersWithIssues.length}
                </span>
              </div>

              <div className="space-y-1 max-h-24 overflow-y-auto">
                {encodersWithIssues.slice(0, 3).map((encoder) => {
                  const encoderInfo = getEncoder(encoder.encoderID);
                  return (
                    <Link
                      key={encoder.encoderID}
                      to="/encoders/$encoderID"
                      params={{ encoderID: encoder.encoderID }}
                      className="flex items-center gap-2 p-1.5 rounded hover:bg-muted text-xs"
                    >
                      <span
                        className={cn(
                          'h-2 w-2 rounded-full flex-shrink-0',
                          encoder.state === 'alert' ? 'bg-red-500' : 'bg-amber-500'
                        )}
                      />
                      <span className="truncate flex-1">
                        {encoderInfo?.clientName && (
                          <span className="text-muted-foreground">{encoderInfo.clientName} / </span>
                        )}
                        {encoder.simpleName || encoderInfo?.simpleName || encoder.encoderID}
                      </span>
                      <span className="text-muted-foreground">
                        {encoder.events.length} events
                      </span>
                    </Link>
                  );
                })}
                {encodersWithIssues.length > 3 && (
                  <p className="text-xs text-muted-foreground py-1">
                    +{encodersWithIssues.length - 3} more
                  </p>
                )}
              </div>
            </div>
            <Separator />
          </>
        )}

        {/* Anomalies Section - only show if there are alerts */}
        {alertCount > 0 && (
          <>
            <div className="p-3">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <span className="text-sm font-medium">Anomalies</span>
                <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                  {alertCount}
                </span>
                {mutedCount > 0 && (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground ml-auto">
                    <VolumeX className="h-3 w-3" />
                    {mutedCount} muted
                  </span>
                )}
              </div>

              <div className="space-y-1 max-h-32 overflow-y-auto">
                {alerts.slice(0, 5).map((alert) => {
                  const encoder = getEncoder(alert.encoderId);
                  const displayName = encoder?.simpleName || alert.streamName || alert.encoderId;
                  return (
                    <Link
                      key={`${alert.encoderId}-${alert.alertType}`}
                      to="/encoders/$encoderID"
                      params={{ encoderID: alert.encoderId }}
                      className="flex items-center gap-2 p-1.5 rounded hover:bg-muted text-xs"
                    >
                      <span
                        className={cn(
                          'h-2 w-2 rounded-full flex-shrink-0',
                          alert.severity === 'critical' ? 'bg-red-500' :
                          alert.severity === 'warning' ? 'bg-amber-500' :
                          'bg-blue-500'
                        )}
                      />
                      <span className="truncate flex-1">{displayName}</span>
                      <span className="text-muted-foreground truncate">
                        {ALERT_TYPE_LABELS[alert.alertType] || alert.alertType}
                      </span>
                    </Link>
                  );
                })}
                {alerts.length > 5 && (
                  <p className="text-xs text-muted-foreground py-1">
                    +{alerts.length - 5} more
                  </p>
                )}
              </div>
            </div>
            {triageCount > 0 && <Separator />}
          </>
        )}

        {/* Triage Section - only show if there are active sessions */}
        {triageCount > 0 && (
          <div className="p-3">
            <div className="flex items-center gap-2 mb-2">
              <Stethoscope className="h-4 w-4 text-red-600" />
              <span className="text-sm font-medium">In Triage</span>
              <span className="ml-auto text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">
                {triageCount}
              </span>
            </div>

            <div className="space-y-1 max-h-32 overflow-y-auto">
              {triageSessions.slice(0, 5).map((session) => {
                const encoder = getEncoder(session.encoderId);
                const displayName = encoder?.simpleName || session.streamName || session.encoderId;
                return (
                  <Link
                    key={session.encoderId}
                    to="/encoders/$encoderID"
                    params={{ encoderID: session.encoderId }}
                    className="flex items-center gap-2 p-1.5 rounded hover:bg-muted text-xs"
                  >
                    <span className="truncate flex-1">{displayName}</span>
                    <span className="text-muted-foreground">
                      {formatTriageDuration(session.startedAt)}
                    </span>
                  </Link>
                );
              })}
              {triageSessions.length > 5 && (
                <p className="text-xs text-muted-foreground py-1">
                  +{triageSessions.length - 5} more
                </p>
              )}
            </div>
          </div>
        )}

        {/* Empty state when nothing to show */}
        {encodersWithIssues.length === 0 && alertCount === 0 && triageCount === 0 && (
          <div className="p-3">
            <p className="text-xs text-muted-foreground text-center py-2">No active issues</p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
